import { AlertTitle, ColoredAlert, Input, Separator, showDialog, Switch, AsyncButton } from '@bpinternal/ui-kit-next'
import { AUTO_RECHARGE_SUPPORTED_ADDON_TYPES } from '../types'
import { CircleAlert } from 'lucide-react'
import { cn } from '~/utils/cn'
import { useSetAutoRechargeSettings } from '../hooks/useSetAutoRechargeSettings'
import { useAddons } from '../hooks/useAddons'
import { useState } from 'react'
import { Boundary } from '~/components'
import { AutoRechargeStatus } from '~/features/billing/components'
import {
  isAutoRechargeLimitReached,
  isAutoRechargePaymentFailed,
  getAddonInitialAutoRechargeLimit,
  DEFAULT_AUTO_RECHARGE_THRESHOLD,
} from '../utils'

const MAX_LIMIT = 999

export const showAddonAutoRechargeDialog = (addonProductId: string, workspaceId: string) => {
  return showDialog(
    ({ close }) => (
      <Boundary>
        <AddonAutoRechargeDialog addonProductId={addonProductId} workspaceId={workspaceId} close={close} />
      </Boundary>
    ),
    {
      size: 'max-w-2xl',
      title: `Auto Recharge`,
    }
  )
}

type AddonAutoRechargeDialogProps = {
  addonProductId: string
  workspaceId: string
  close: () => void
}
const AddonAutoRechargeDialog = ({ addonProductId, workspaceId, close }: AddonAutoRechargeDialogProps) => {
  const { addons } = useAddons(workspaceId)
  const addon = addons.find((a) => a.productId === addonProductId)
  const { mutateAsync: updateAutoRechargeSettings, isPending: updateAutoRechargeSettingsPending } =
    useSetAutoRechargeSettings({
      workspaceId,
    })

  const initialLimit = getAddonInitialAutoRechargeLimit(addon)
  const [limitInput, setLimitInput] = useState(String(initialLimit))
  const limit = limitInput === '' ? initialLimit : Math.max(1, Number(limitInput) || 1)
  const isLimitValid =
    limitInput !== '' &&
    Number(limitInput) > 0 &&
    Number(limitInput) <= MAX_LIMIT &&
    limit >= (addon?.currentQuantity ?? 0)

  if (!addon || !addon.autoRecharge?.settings) return null
  const settings = addon.autoRecharge.settings

  const quotaName = addon.quotaName as (typeof AUTO_RECHARGE_SUPPORTED_ADDON_TYPES)[number]

  const isLimitReached = isAutoRechargeLimitReached(settings)
  const displayEnabled = settings.enabled ? true : isLimitReached

  const onUpdateAutoRechargeSettings = async (enabled: boolean) => {
    if (enabled) {
      await updateAutoRechargeSettings({
        settings: {
          [quotaName]: {
            enabled: true,
            limit,
            threshold: DEFAULT_AUTO_RECHARGE_THRESHOLD,
          },
        },
      })
      return
    }

    await updateAutoRechargeSettings({
      settings: {
        [quotaName]: {
          enabled: false,
        },
      },
    })
  }

  return (
    <div className="font-sm flex flex-row gap-6 text-muted-foreground">
      <div className="flex-1 space-y-5">
        <div className="space-y-4">
          <p>
            Automatically purchase additional {addon.displayName} when your Workspace is close to its usage limit.{' '}
            <a
              href="https://www.botpress.com/docs/learn/get-started/dashboard/workspace/settings/introduction#billing"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Learn more
            </a>
          </p>
          <div className="flex flex-row justify-between gap-2">
            <AutoRechargeStatus settings={settings} textClassName="text-foreground" />
            <Switch checked={displayEnabled} onCheckedChange={onUpdateAutoRechargeSettings} />
          </div>
        </div>
        <Separator />
        {displayEnabled ? (
          <div className="space-y-2">
            <div className="space-y-2">
              <p className="font-medium text-foreground">Limit Spending</p>
              <p>Set a cap to stop Auto Recharges after reaching your limit.</p>
            </div>
            {isLimitReached && (
              <ColoredAlert color="amber">
                <CircleAlert className="size-4" />
                <AlertTitle className="text-sm font-normal">
                  Limit Reached. <br /> Increase to re-enable auto recharge
                </AlertTitle>
              </ColoredAlert>
            )}
            <div className="space-y-0.5">
              <div className="min-h-[20px]">
                {limit < addon.currentQuantity && (
                  <p className="text-xs text-red-500">Limit cannot be less than add-on quantity</p>
                )}
              </div>
              <div className="flex flex-col gap-3 md:flex-row">
                <div className="relative flex-grow">
                  <Input
                    id="limit"
                    type="number"
                    className="pr-[160px] text-foreground"
                    value={limitInput}
                    onChange={(e) => {
                      setLimitInput(e.target.value.slice(0, String(MAX_LIMIT).length))
                    }}
                  />
                  <label htmlFor="limit" className="absolute bottom-1 right-2 top-1 flex select-none items-center">
                    Total Add-on quantity
                  </label>
                </div>
                <AsyncButton
                  className="w-full md:w-fit"
                  variant="default"
                  loading={updateAutoRechargeSettingsPending}
                  disabled={
                    updateAutoRechargeSettingsPending ||
                    !isLimitValid ||
                    (settings.enabled && limit === settings.limit) ||
                    (isLimitReached && limit === addon.currentQuantity)
                  }
                  onClick={async () => {
                    await onUpdateAutoRechargeSettings(true)
                    close()
                  }}
                >
                  Update
                </AsyncButton>
              </div>
            </div>
          </div>
        ) : isAutoRechargePaymentFailed(settings) ? (
          <ColoredAlert color="red">
            <CircleAlert className="size-4" />
            <AlertTitle className="font-normal">Auto Recharge is disabled due to a payment error.</AlertTitle>
          </ColoredAlert>
        ) : (
          <ColoredAlert color="amber">
            <CircleAlert className="size-4" />
            <AlertTitle className="font-normal">
              Disabling Auto Recharge can lead to interruptions when limits are reached.
            </AlertTitle>
          </ColoredAlert>
        )}
      </div>
      <Graph autoRechargeEnabled={displayEnabled} />
    </div>
  )
}

const Graph = ({ autoRechargeEnabled }: { autoRechargeEnabled: boolean }) => {
  return (
    <div className="hidden min-h-[258px] w-[280px] self-stretch rounded-[10px] bg-gray-50 px-4 md:block">
      <div className="relative h-full w-full">
        <div className="flex h-full flex-row items-end gap-3 px-[26px]">
          <div className="h-[124px] w-10 rounded-t-lg bg-gray-200" />
          <div className="h-[119px] w-10 rounded-t-lg bg-gray-200" />
          <AnimatedBar height={233} autoRechargeEnabled={autoRechargeEnabled} />
          <AnimatedBar height={201} autoRechargeEnabled={autoRechargeEnabled} />
        </div>
        <hr className=" absolute bottom-[174px] w-full border-t border-dashed border-gray-400" />
      </div>
    </div>
  )
}

const AnimatedBar = ({ height, autoRechargeEnabled }: { height: number; autoRechargeEnabled: boolean }) => {
  return (
    <div
      className={cn(
        'w-10 rounded-t-lg transition-all',
        autoRechargeEnabled
          ? 'bg-[linear-gradient(to_bottom,theme(colors.gray.400)_0%,theme(colors.gray.400)_calc(100%-174px),theme(colors.gray.200)_calc(100%-174px),theme(colors.gray.200)_100%)]'
          : 'bg-gradient-to-b from-red-500/40 to-red-500/0'
      )}
      style={{
        height: autoRechargeEnabled ? `${height}px` : '174px',
      }}
    />
  )
}
